package vegasful.admin.components

import androidx.compose.runtime.*
import kotlinx.datetime.*
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text

/**
 * Data class to hold cron schedule information
 */
data class CronSchedule(
    val hour: Int = 0,
    val minute: Int = 0,
    val daysOfWeek: Set<Int> = emptySet() // 1=Sunday, 2=Monday, etc.
)

/**
 * Helper function to parse EventBridge cron expression and convert to local time
 * EventBridge format: minutes hours day-of-month month day-of-week year
 */
private fun parseCronExpression(cron: String?, timeZone: TimeZone = TimeZone.Companion.UTC): CronSchedule? {
    if (cron.isNullOrBlank()) return null

    val parts = cron.trim().split("\\s+".toRegex())
    if (parts.size != 6) return null

    return try {
        val utcMinute = parts[0].toIntOrNull() ?: 0
        val utcHour = parts[1].toIntOrNull() ?: 0

        // Convert UTC to local time using proper timezone handling
        val today = Clock.System.todayIn(TimeZone.Companion.UTC)
        val utcDateTime = today.atTime(utcHour, utcMinute)
        val utcInstant = utcDateTime.toInstant(TimeZone.Companion.UTC)
        val localDateTime = utcInstant.toLocalDateTime(timeZone)

        // Parse day of week (5th field, 0-indexed)
        val dayOfWeekPart = parts[4]
        val daysOfWeek = when (dayOfWeekPart) {
            "*" -> (1..7).toSet()
            "?" -> (1..7).toSet()
            else -> {
                // Handle comma-separated values and ranges
                dayOfWeekPart.split(",").flatMap { part ->
                    if (part.contains("-")) {
                        val (start, end) = part.split("-").map { it.toInt() }
                        (start..end).toList()
                    } else {
                        listOf(part.toInt())
                    }
                }.toSet()
            }
        }

        CronSchedule(localDateTime.hour, localDateTime.minute, daysOfWeek)
    } catch (_: Exception) {
        null
    }
}

/**
 * Helper function to generate EventBridge cron expression (converts local time to UTC)
 */
private fun generateCronExpression(schedule: CronSchedule, timeZone: TimeZone = TimeZone.Companion.UTC): String {
    // Convert local time to UTC using proper timezone handling
    val today = Clock.System.todayIn(timeZone)
    val localDateTime = today.atTime(schedule.hour, schedule.minute)
    val localInstant = localDateTime.toInstant(timeZone)
    val utcDateTime = localInstant.toLocalDateTime(TimeZone.Companion.UTC)

    val dayOfMonth = "?"
    val month = "*"
    val year = "*"

    val dayOfWeek = if (schedule.daysOfWeek.size == 7) {
        "*"
    } else {
        schedule.daysOfWeek.sorted().joinToString(",")
    }

    return "${utcDateTime.minute} ${utcDateTime.hour} $dayOfMonth $month $dayOfWeek $year"
}

/**
 * A component for editing cron expressions with a user-friendly interface
 */
@Composable
fun cronScheduleEditor(
    initialCron: String?,
    timeZone: TimeZone = TimeZone.Companion.UTC,
    onCronChange: (String?) -> Unit
) {
    var schedule by remember { mutableStateOf(parseCronExpression(initialCron, timeZone) ?: CronSchedule()) }
    var hour by remember { mutableStateOf(schedule.hour) }
    var minute by remember { mutableStateOf(schedule.minute) }
    var selectedDays by remember { mutableStateOf(schedule.daysOfWeek) }

    // Day names for display
    val dayNames = listOf("Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday")
    val dayValues = (1..7).toList()

    // Update schedule when inputs change
    LaunchedEffect(hour, minute, selectedDays) {
        val hourInt = hour
        val minuteInt = minute

        println("Selected days: ${selectedDays.size}")

        if (hourInt in 0..23 && minuteInt in 0..59) {
            schedule = CronSchedule(hourInt, minuteInt, selectedDays)
            val cronExpression = if (selectedDays.isEmpty()) null else generateCronExpression(schedule, timeZone)
            onCronChange(cronExpression)
        }
    }

    // Update when initial cron changes
    LaunchedEffect(initialCron) {
        val parsed = parseCronExpression(initialCron, timeZone)
        if (parsed != null) {
            schedule = parsed
            hour = schedule.hour
            minute = schedule.minute
            selectedDays = schedule.daysOfWeek
        }
    }

    // Cron expression preview
    if (selectedDays.isNotEmpty()) {
        dialogField {
            Text(generateCronExpression(schedule, timeZone))
        }
    }

    dialogField {
        Div({
            style {
                display(DisplayStyle.Companion.Flex)
                flexWrap(FlexWrap.Companion.Wrap)
                gap(0.5.cssRem)
            }
        }) {
            dayNames.forEachIndexed { index, dayName ->
                val dayValue = dayValues[index]
                val isSelected = selectedDays.contains(dayValue)

                Label(attrs = {
                    style {
                        display(DisplayStyle.Companion.Flex)
                        alignItems(AlignItems.Companion.Center)
                        gap(0.25.cssRem)
                        fontWeight("normal")
                    }
                }) {
                    Input(InputType.Checkbox) {
                        checked(isSelected)
                        onInput { event ->
                            selectedDays = if (event.value) {
                                selectedDays + dayValue
                            } else {
                                selectedDays - dayValue
                            }
                        }
                    }
                    Text(dayName)
                }
            }
        }
    }

    if (selectedDays.isNotEmpty()) {
        dialogField("Time (Local)") {
            Input(InputType.Time, {
                value("${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}")
                onChange {
                    val elements = it.value.split(":")
                    val newHour = elements.firstOrNull()?.toIntOrNull() ?: 0
                    val newMinute = elements.getOrNull(1)?.toIntOrNull() ?: 0
                    hour = newHour
                    minute = newMinute
                }
            })
        }
    }
}